import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { apiError } from "@/lib/platform";
import { Panel } from "@/components/rights/RightsBadges";

const TYPES = [
  ["activity", "RoPA activities", (x) => [x.org_name, x.categories?.join(", "), x.volume_band].filter(Boolean).join(" · ")],
  ["asset", "RoPA assets", (x) => [x.asset_type, x.hosting, x.org_name].filter(Boolean).join(" · ")],
  ["data_class", "RoPA data classes", (x) => [x.sensitivity && `${x.sensitivity} sensitivity`, x.category].filter(Boolean).join(" · ")],
  ["party", "RoPA third parties", (x) => [x.party_type, x.dpa_status && `DPA ${x.dpa_status}`, x.country].filter(Boolean).join(" · ")],
];
const SENS = { High: "bg-red-50 text-red-700", Medium: "bg-amber-50 text-amber-800", Low: "bg-slate-100 text-slate-600" };

const AddDialog = ({ type, onClose, v, onDone }) => {
  const [q, setQ] = useState("");
  const [opts, setOpts] = useState([]);
  const [sel, setSel] = useState([]);
  const [cascade, setCascade] = useState(true);
  const linked = new Set((v.links[type] || []).map((x) => x.id));
  useEffect(() => {
    if (!type) return;
    const t = setTimeout(() => api.get("/vendors/ropa-options", { params: { link_type: type, q: q || undefined } })
      .then(({ data }) => setOpts(data.options)).catch(() => {}), 200);
    return () => clearTimeout(t);
  }, [type, q]);
  useEffect(() => { setSel([]); setQ(""); }, [type]);
  const submit = async () => {
    try {
      const { data } = await api.post(`/vendors/${v.id}/links`, { link_type: type, ropa_ids: sel, cascade });
      const n = Object.entries(data.added).filter(([, c]) => c).map(([k, c]) => `${c} ${k.replace("_", " ")}`).join(", ");
      toast.success(n ? `Linked ${n}` : "Already linked");
      onClose(); onDone();
    } catch (e) { toast.error(apiError(e, "Could not link")); }
  };
  const label = TYPES.find((t) => t[0] === type)?.[1];
  return (
    <Dialog open={!!type} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="ropa-link-dialog">
        <DialogHeader>
          <DialogTitle>Link {label}</DialogTitle>
          <DialogDescription>Choose records from this organization's RoPA registers.</DialogDescription>
        </DialogHeader>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" data-testid="ropa-link-search" />
        <div className="max-h-[320px] overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100" data-testid="ropa-link-options">
          {opts.length === 0 && <div className="text-xs text-slate-500 p-3">No matching records.</div>}
          {opts.map((o) => (
            <label key={o.id} className={`flex items-start gap-2.5 px-3 py-2 text-xs cursor-pointer hover:bg-slate-50 ${linked.has(o.id) ? "opacity-50" : ""}`}>
              <Checkbox checked={sel.includes(o.id) || linked.has(o.id)} disabled={linked.has(o.id)} data-testid={`ropa-option-${o.id}`}
                        onCheckedChange={(c) => setSel((s) => (c ? [...s, o.id] : s.filter((x) => x !== o.id)))} />
              <span><span className="font-medium text-[#003366]">{o.name}</span>
                <span className="block text-[11px] text-slate-500">{[o.org_name, o.detail].filter(Boolean).join(" · ")}</span></span>
            </label>
          ))}
        </div>
        {type === "activity" && (
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <Checkbox checked={cascade} onCheckedChange={(c) => setCascade(!!c)} data-testid="ropa-link-cascade" />
            Also link the activity's data classes and assets
          </label>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button disabled={!sel.length} onClick={submit} data-testid="ropa-link-submit">Link {sel.length || ""}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const RopaLinksPanel = ({ v, canEdit, onChanged }) => {
  const [adding, setAdding] = useState(null);
  const remove = async (type, id) => {
    try {
      await api.delete(`/vendors/${v.id}/links/${type}/${id}`);
      toast.success("Link removed, risk recalculated");
      onChanged();
    } catch (e) { toast.error(apiError(e, "Could not remove")); }
  };
  return (
    <Panel eyebrow="Integrated with RoPA" title="RoPA links" testId="ropa-links-panel">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {TYPES.map(([type, label, detail]) => (
          <div key={type} className="border border-slate-200 rounded-lg" data-testid={`ropa-links-${type}`}>
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100">
              <span className="text-sm font-semibold text-[#003366]">{label} <span className="font-mono text-slate-400 text-xs">{v.links[type].length}</span></span>
              {canEdit && <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => setAdding(type)} data-testid={`ropa-link-add-${type}`}>
                <Plus className="w-3 h-3 mr-1" /> Link</Button>}
            </div>
            <ul className="divide-y divide-slate-50 max-h-[260px] overflow-y-auto">
              {v.links[type].length === 0 && <li className="text-xs text-slate-500 px-4 py-3">Nothing linked.</li>}
              {v.links[type].map((x) => (
                <li key={x.id} className="flex items-center gap-2 px-4 py-2 text-xs" data-testid={`ropa-link-${type}-${x.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-[#003366] truncate">{x.name}</div>
                    <div className="text-[11px] text-slate-500 truncate">{detail(x)}</div>
                  </div>
                  {type === "data_class" && x.sensitivity && <span className={`text-[10px] px-1.5 py-0.5 rounded ${SENS[x.sensitivity]}`}>{x.sensitivity}</span>}
                  {canEdit && <button onClick={() => remove(type, x.id)} className="text-slate-300 hover:text-red-600" data-testid={`ropa-unlink-${type}-${x.id}`}>
                    <X className="w-3.5 h-3.5" /></button>}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <AddDialog type={adding} onClose={() => setAdding(null)} v={v} onDone={onChanged} />
    </Panel>
  );
};
